import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../api/axios";
import "./MyPayments.css";

const TABS = [
  { key: "bookings", label: "Bookings" },
  { key: "payments", label: "Payments" },
  { key: "receipts", label: "Receipts" },
  { key: "installments", label: "Installments" },
];

const STAGE_LABELS = {
  booking_fee: "Booking fee",
  down_payment: "Down payment",
  installment: "Installment",
  late_fee: "Late fee",
};

const PAYMENT_STATUS = {
  success: { label: "Paid", className: "bg-success" },
  pending: { label: "Awaiting confirmation", className: "bg-warning text-dark" },
  failed: { label: "Failed", className: "bg-danger" },
};

const INSTALLMENT_STATUS = {
  paid: { label: "Paid", className: "bg-success" },
  partial: { label: "Partly paid", className: "bg-info text-dark" },
  pending: { label: "Pending", className: "bg-secondary" },
  overdue: { label: "Overdue", className: "bg-danger" },
  cancelled: { label: "Cancelled", className: "bg-dark" },
};

export const formatMoney = (value) =>
  `৳ ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
};

const stageLabel = (stage) => STAGE_LABELS[stage] || "Payment";

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

/** Fetch a PDF with the signed-in user's token (a bare link would not carry it) and save it. */
async function downloadPdf(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const href = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to start the download before the blob URL goes away.
  setTimeout(() => window.URL.revokeObjectURL(href), 1000);
}

const Spinner = () => (
  <div className="d-flex justify-content-center my-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const EmptyState = ({ title, children }) => (
  <div className="mp-empty text-center text-muted py-5">
    <h3 className="h5">{title}</h3>
    {children && <p className="mb-0">{children}</p>}
  </div>
);

const PaymentBadge = ({ payment }) => {
  const status = PAYMENT_STATUS[payment.status] || { label: payment.status || "-", className: "bg-secondary" };
  return (
    <>
      <span className={`badge ${status.className}`}>{status.label}</span>
      {payment.refunded && <span className="badge bg-dark ms-1">Refunded</span>}
    </>
  );
};

export default function MyPayments() {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [scheduleBookingId, setScheduleBookingId] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const [receipt, setReceipt] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [bookingsRes, paymentsRes] = await Promise.all([
        api.get("/api/my/bookings"),
        api.get("/api/my/payments"),
      ]);
      setBookings(bookingsRes.data?.data || []);
      setPayments(paymentsRes.data?.data || []);
    } catch (err) {
      console.error(err);
      setFailed(true);
      toast.error(errorMessage(err, "Could not load your payments."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const bookingsWithPlan = useMemo(
    () => bookings.filter((b) => b.installments_count > 0),
    [bookings],
  );

  // Default the schedule to the first booking that has an installment plan.
  useEffect(() => {
    if (scheduleBookingId === null && bookingsWithPlan.length > 0) {
      setScheduleBookingId(bookingsWithPlan[0].id);
    }
  }, [bookingsWithPlan, scheduleBookingId]);

  useEffect(() => {
    if (tab !== "installments" || scheduleBookingId === null) return undefined;
    let cancelled = false;
    setScheduleLoading(true);
    setSchedule(null);
    api
      .get(`/api/my/bookings/${scheduleBookingId}/installments`)
      .then((res) => !cancelled && setSchedule(res.data))
      .catch((err) => {
        console.error(err);
        if (!cancelled) toast.error(errorMessage(err, "Could not load the installment schedule."));
      })
      .finally(() => !cancelled && setScheduleLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, scheduleBookingId]);

  // One receipt per invoice; several payments can share one.
  const receipts = useMemo(() => {
    const byInvoice = new Map();
    payments.forEach((p) => {
      if (!p.invoice_id || p.status !== "success") return;
      const entry = byInvoice.get(p.invoice_id) || {
        invoice_id: p.invoice_id,
        invoice_number: p.invoice_number,
        booking_id: p.booking_id,
        property_name: p.property_name,
        unit_number: p.unit_number,
        date: p.date,
        amount: 0,
        stages: [],
      };
      entry.amount += Number(p.amount || 0);
      if (!entry.stages.includes(stageLabel(p.stage))) entry.stages.push(stageLabel(p.stage));
      if (p.date && (!entry.date || new Date(p.date) > new Date(entry.date))) entry.date = p.date;
      byInvoice.set(p.invoice_id, entry);
    });
    return Array.from(byInvoice.values()).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [payments]);

  const totals = useMemo(
    () => ({
      total: bookings.reduce((sum, b) => sum + Number(b.total_amount || 0), 0),
      paid: bookings.reduce((sum, b) => sum + Number(b.paid_amount || 0), 0),
      due: bookings.reduce((sum, b) => sum + Number(b.due_amount || 0), 0),
      overdue: bookings.reduce((sum, b) => sum + Number(b.overdue_installments_count || 0), 0),
    }),
    [bookings],
  );

  const runDownload = async (key, url, filename, failMessage) => {
    setBusyKey(key);
    try {
      await downloadPdf(url, filename);
    } catch (err) {
      console.error(err);
      toast.error(failMessage);
    } finally {
      setBusyKey(null);
    }
  };

  const downloadReceipt = (invoiceId, invoiceNumber) =>
    runDownload(
      `receipt-${invoiceId}`,
      `/api/invoices/${invoiceId}/pdf`,
      `Receipt-${invoiceNumber || invoiceId}.pdf`,
      "Could not download the receipt.",
    );

  const downloadSchedule = (bookingId) =>
    runDownload(
      `schedule-${bookingId}`,
      `/api/installments/report/${bookingId}`,
      `installments_booking_${bookingId}.pdf`,
      "Could not download the installment schedule.",
    );

  const openReceipt = async (invoiceId) => {
    setReceipt({ id: invoiceId, data: null });
    setReceiptLoading(true);
    try {
      const res = await api.get(`/api/invoices/${invoiceId}`);
      setReceipt({ id: invoiceId, data: res.data?.data || null });
    } catch (err) {
      console.error(err);
      toast.error(errorMessage(err, "Could not load the receipt."));
      setReceipt(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  const openSchedule = (bookingId) => {
    setScheduleBookingId(bookingId);
    setTab("installments");
  };

  const renderBookings = () => {
    if (bookings.length === 0) {
      return (
        <EmptyState title="No bookings yet">
          When you book a unit, its payment status shows up here.{" "}
          <Link to="/properties_available">Browse available properties</Link>
        </EmptyState>
      );
    }
    return (
      <div className="row g-3">
        {bookings.map((b) => (
          <div className="col-12 col-md-6 col-xl-4" key={b.id}>
            <div className="card mp-booking-card h-100 shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <div className="fw-bold">{b.property_name || "Property"}</div>
                    <small className="text-muted">Unit {b.unit_number || "-"} - Booking #{b.id}</small>
                  </div>
                  <span className="badge bg-light text-dark border text-capitalize">{b.status || "-"}</span>
                </div>
                <dl className="mp-amounts mb-2">
                  <div><dt>Total</dt><dd>{formatMoney(b.total_amount)}</dd></div>
                  <div><dt>Paid</dt><dd className="text-success">{formatMoney(b.paid_amount)}</dd></div>
                  <div><dt>Due</dt><dd className="text-danger fw-bold">{formatMoney(b.due_amount)}</dd></div>
                </dl>
                {b.overdue_installments_count > 0 && (
                  <div className="alert alert-danger py-1 px-2 small mb-2">
                    {b.overdue_installments_count} overdue installment{b.overdue_installments_count > 1 ? "s" : ""}
                  </div>
                )}
                {b.next_due_date && b.overdue_installments_count === 0 && (
                  <div className="small text-muted mb-2">Next installment due {formatDate(b.next_due_date)}</div>
                )}
                <div className="d-flex flex-wrap gap-2">
                  <Link to={`/properties_booked/${b.id}`} className="btn btn-sm btn-outline-primary">
                    Details
                  </Link>
                  {b.installments_count > 0 && (
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => openSchedule(b.id)}>
                      Installment schedule
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPayments = () => {
    if (payments.length === 0) {
      return <EmptyState title="No payments yet">Payments you make for your bookings are listed here.</EmptyState>;
    }
    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle text-nowrap">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Booking</th>
              <th>Stage</th>
              <th>Method</th>
              <th className="text-end">Amount</th>
              <th>Status</th>
              <th className="text-center">Receipt</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className={p.refunded ? "table-secondary" : undefined}>
                <td>{formatDate(p.date)}</td>
                <td>
                  <div>{p.property_name || "-"}</div>
                  <small className="text-muted">Unit {p.unit_number || "-"} - #{p.booking_id}</small>
                </td>
                <td>
                  {stageLabel(p.stage)}
                  {p.installment_title && <div className="small text-muted">{p.installment_title}</div>}
                </td>
                <td className="text-capitalize">{p.method || "-"}</td>
                <td className="text-end">
                  {formatMoney(p.amount)}
                  {p.refunded && p.refund_amount && (
                    <div className="small text-muted">Refunded {formatMoney(p.refund_amount)}</div>
                  )}
                </td>
                <td><PaymentBadge payment={p} /></td>
                <td className="text-center">
                  {p.invoice_id ? (
                    <button type="button" className="btn btn-sm btn-link" onClick={() => openReceipt(p.invoice_id)}>
                      {p.invoice_number || "View"}
                    </button>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderReceipts = () => {
    if (receipts.length === 0) {
      return <EmptyState title="No receipts yet">A receipt appears here once a payment is confirmed.</EmptyState>;
    }
    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle text-nowrap">
          <thead className="table-light">
            <tr>
              <th>Receipt</th>
              <th>Date</th>
              <th>Booking</th>
              <th>For</th>
              <th className="text-end">Amount</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {receipts.map((r) => (
              <tr key={r.invoice_id}>
                <td className="fw-semibold">{r.invoice_number || `#${r.invoice_id}`}</td>
                <td>{formatDate(r.date)}</td>
                <td>
                  <div>{r.property_name || "-"}</div>
                  <small className="text-muted">Unit {r.unit_number || "-"}</small>
                </td>
                <td>{r.stages.join(", ")}</td>
                <td className="text-end">{formatMoney(r.amount)}</td>
                <td className="text-end">
                  <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => openReceipt(r.invoice_id)}>
                    View
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    disabled={busyKey === `receipt-${r.invoice_id}`}
                    onClick={() => downloadReceipt(r.invoice_id, r.invoice_number)}
                  >
                    {busyKey === `receipt-${r.invoice_id}` ? "Downloading..." : "Download PDF"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSchedule = () => {
    if (bookingsWithPlan.length === 0) {
      return (
        <EmptyState title="No installment plan yet">
          Your installment schedule appears here once a plan is created for a booking.
        </EmptyState>
      );
    }
    return (
      <>
        <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
          <div>
            <label htmlFor="mp-booking-select" className="form-label small text-muted mb-1">Booking</label>
            <select
              id="mp-booking-select"
              className="form-select"
              value={scheduleBookingId ?? ""}
              onChange={(e) => setScheduleBookingId(Number(e.target.value))}
            >
              {bookingsWithPlan.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.property_name || "Property"} - Unit {b.unit_number || "-"} (#{b.id})
                </option>
              ))}
            </select>
          </div>
          {schedule && (
            <button
              type="button"
              className="btn btn-success ms-md-auto"
              disabled={busyKey === `schedule-${schedule.booking.id}`}
              onClick={() => downloadSchedule(schedule.booking.id)}
            >
              {busyKey === `schedule-${schedule.booking.id}` ? "Downloading..." : "Download schedule PDF"}
            </button>
          )}
        </div>

        {scheduleLoading && <Spinner />}
        {!scheduleLoading && schedule && schedule.installments.length === 0 && (
          <EmptyState title="No installments on this booking" />
        )}
        {!scheduleLoading && schedule && schedule.installments.length > 0 && (
          <>
            {schedule.totals.overdue_count > 0 && (
              <div className="alert alert-danger py-2" role="alert">
                {schedule.totals.overdue_count} installment{schedule.totals.overdue_count > 1 ? "s are" : " is"} overdue.
              </div>
            )}
            <div className="table-responsive">
              <table className="table align-middle text-nowrap">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Due date</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">Paid</th>
                    <th className="text-end">Due</th>
                    <th className="text-end">Late fee</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.installments.map((row) => {
                    const status = INSTALLMENT_STATUS[row.is_overdue ? "overdue" : row.status] || {
                      label: row.status,
                      className: "bg-secondary",
                    };
                    return (
                      <tr key={row.id} className={row.is_overdue ? "table-danger" : undefined} data-testid={`installment-${row.id}`}>
                        <td>{row.title || row.sequence}</td>
                        <td>{formatDate(row.due_date)}</td>
                        <td className="text-end">{formatMoney(row.amount)}</td>
                        <td className="text-end text-success">{formatMoney(row.paid_amount)}</td>
                        <td className="text-end">{formatMoney(row.due_amount)}</td>
                        <td className="text-end">{Number(row.late_fee) > 0 ? formatMoney(row.late_fee) : "-"}</td>
                        <td>
                          <span className={`badge ${status.className}`}>{status.label}</span>
                          {row.status === "paid" && row.paid_date && (
                            <div className="small text-muted">{formatDate(row.paid_date)}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td colSpan={2}>Total</td>
                    <td className="text-end">{formatMoney(schedule.totals.amount)}</td>
                    <td className="text-end">{formatMoney(schedule.totals.paid_amount)}</td>
                    <td className="text-end">{formatMoney(schedule.totals.due_amount)}</td>
                    <td className="text-end">{formatMoney(schedule.totals.late_fee)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </>
    );
  };

  const renderTab = () => {
    if (tab === "payments") return renderPayments();
    if (tab === "receipts") return renderReceipts();
    if (tab === "installments") return renderSchedule();
    return renderBookings();
  };

  const invoice = receipt?.data;

  return (
    <div className="container mp-page">
      <h2 className="fw-bold mb-1">My Payments</h2>
      <p className="text-muted">Your bookings, payment history, receipts, and installment schedules in one place.</p>

      {loading ? (
        <Spinner />
      ) : failed ? (
        <div className="alert alert-danger d-flex flex-wrap align-items-center gap-3" role="alert">
          <span>We could not load your payments.</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={load}>
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="row g-3 mb-4">
            {[
              { label: "Total booked", value: formatMoney(totals.total) },
              { label: "Paid", value: formatMoney(totals.paid), className: "text-success" },
              { label: "Due", value: formatMoney(totals.due), className: "text-danger" },
              { label: "Overdue installments", value: totals.overdue, className: totals.overdue > 0 ? "text-danger" : "" },
            ].map((card) => (
              <div className="col-6 col-lg-3" key={card.label}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="small text-muted">{card.label}</div>
                    <div className={`fs-5 fw-bold text-nowrap ${card.className || ""}`}>{card.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <ul className="nav nav-tabs mp-tabs mb-3" role="tablist">
            {TABS.map(({ key, label }) => (
              <li className="nav-item" role="presentation" key={key}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={`nav-link ${tab === key ? "active" : ""}`}
                  onClick={() => setTab(key)}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">{renderTab()}</div>
          </div>
        </>
      )}

      <Modal show={Boolean(receipt)} onHide={() => setReceipt(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Receipt{invoice?.invoice_number ? ` ${invoice.invoice_number}` : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {receiptLoading || !invoice ? (
            <Spinner />
          ) : (
            <div className="mp-receipt">
              <div className="row mb-3">
                <div className="col-sm-6">
                  <div className="small text-muted">Date</div>
                  <div>{formatDate(invoice.invoice_date)}</div>
                </div>
                <div className="col-sm-6">
                  <div className="small text-muted">Property / unit</div>
                  <div>{invoice.property?.name || "-"} / {invoice.unit?.unit_number || "-"}</div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr><th>Description</th><th className="text-end">Amount</th></tr>
                  </thead>
                  <tbody>
                    {(invoice.items || []).map((item) => (
                      <tr key={item.id}>
                        <td>{item.description}</td>
                        <td className="text-end">{formatMoney(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold"><td>Total</td><td className="text-end">{formatMoney(invoice.total_amount)}</td></tr>
                    <tr><td>Paid</td><td className="text-end text-success">{formatMoney(invoice.paid_amount)}</td></tr>
                    <tr><td>Due</td><td className="text-end">{formatMoney(invoice.due_amount)}</td></tr>
                  </tfoot>
                </table>
              </div>
              {invoice.notes && <p className="small text-muted mb-0">{invoice.notes}</p>}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReceipt(null)}>Close</Button>
          {invoice && (
            <Button
              variant="success"
              disabled={busyKey === `receipt-${invoice.id}`}
              onClick={() => downloadReceipt(invoice.id, invoice.invoice_number)}
            >
              {busyKey === `receipt-${invoice.id}` ? "Downloading..." : "Download PDF"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
